import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Pagination from "../../../../../components/general/Pagination.js";
import "../../../../../components/components/css/RoomsCard.css";

const AdmissionRoomsCard = ({ schoolId, axiosPrivate, roomsApiUrl, initialSelected, onSave, editable }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState(initialSelected || []);
  const [tempSelection, setTempSelection] = useState(initialSelected || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  // Rooms pagination state
  const [allRooms, setAllRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const PAGE_SIZE = 20;

  const fetchRooms = useCallback(async (page = 1) => {
    try {
      setRoomsLoading(true);
      const res = await axiosPrivate.get(roomsApiUrl, {
        params: { page, page_size: PAGE_SIZE }
      });
      const data = res.data;
      setAllRooms(data.results.map((r) => ({ id: r.id, room: r.name })));
      setCurrentPage(data.current_page);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_items);
      setNextPage(data.links.next);
      setPreviousPage(data.links.previous);
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
    } finally {
      setRoomsLoading(false);
    }
  }, [axiosPrivate, roomsApiUrl]);

  // Fetch rooms only when editing starts
  useEffect(() => {
    if (isEditing) {
      fetchRooms(1);
    }
  }, [isEditing]);

  const toggleRoom = (room) => {
    setTempSelection((prev) => {
      const exists = prev.some((r) => r.id === room.id);
      return exists ? prev.filter((r) => r.id !== room.id) : [...prev, room];
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      const roomIds = tempSelection.map((item) => item.id);
      if (onSave) await onSave({ Classes: roomIds });
      setSelectedRooms(tempSelection);
      setIsEditing(false);
    } catch (err) {
      setError("Failed to update. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTempSelection(selectedRooms);
    setIsEditing(false);
    setError(null);
  };

  return (
    <Box className="rooms-card">
      <Box className="rooms-card-header">
        <Typography variant="header" fontWeight="bold">
          {t(`studentDetails.infoCard.titles.Rooms`)}
        </Typography>
        {editable && (
          <>
            {isEditing ? (
              <Box className="rooms-controls">
                <Button onClick={handleSave} disabled={loading} className="save-btn">
                  {t("actions.save")}
                </Button>
                <Button onClick={handleCancel} disabled={loading} className="cancel-btn">
                  {t("actions.cancel")}
                </Button>
              </Box>
            ) : (
              <Button onClick={() => setIsEditing(true)}>{t("actions.edit")}</Button>
            )}
          </>
        )}
      </Box>

      <Box className="rooms-card-body">
        <Box className="rooms-tags-container">
          {isEditing ? (
            <>
              {roomsLoading ? (
                <Typography>Loading...</Typography>
              ) : (
                allRooms.map((room) => {
                  const isSelected = tempSelection.some((r) => r.id === room.id);
                  return (
                    <button
                      key={room.id}
                      className={`room-tag-btn ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleRoom(room)}
                    >
                      {room.room}
                    </button>
                  );
                })
              )}

              {/* Pagination — only shows if more than 1 page */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalItems}
                  onPageChange={(page) => fetchRooms(page)}
                  nextPage={nextPage}
                  previousPage={previousPage}
                  pageSize={PAGE_SIZE}
                />
              )}
            </>
          ) : selectedRooms.length > 0 ? (
            selectedRooms.map((room, i) => (
              <Box key={i} className="rooms-tag">
                {room.room}
              </Box>
            ))
          ) : (
            <Typography>{t("studentDetails.infoCard.titles.noRooms")}</Typography>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ mt: 1, fontSize: "13px" }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AdmissionRoomsCard;